import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import IngredientCard from "@/components/IngredientCard";
import { ProductCard } from "@/components/library/ProductCard";
import { Package, Plus, Trash2, Search, Sparkles, FlaskConical, RefreshCw, Archive, Lightbulb, CheckCircle, RotateCcw, Beaker, SlidersHorizontal, X, PackageX, Loader2, Sun, Moon, AlertTriangle, ShoppingBag, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function ProductLibrary() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Recalculate scores for products with missing scores
  const recalculateScores = async () => {
    setRecalculating(true);
    try {
      const response = await axios.post(`${API}/products/recalculate-scores`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Updated ${response.data.updated_count} products with scores!`);
        fetchProducts(); // Refresh to show new scores
      } else {
        toast.error("Failed to recalculate scores");
      }
    } catch (error) {
      console.error("Recalculate error:", error);
      toast.error("Error recalculating scores");
    } finally {
      setRecalculating(false);
    }
  };

  // Handle URL parameters
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    const status = searchParams.get('status');
    
    if (status) {
      setStatusFilter(status);
    }
    
    if (highlightId && products.length > 0) {
      const product = products.find(p => p.id === highlightId);
      if (product) {
        setSelectedProduct(product);
        navigate('/products', { replace: true });
      }
    }
  }, [searchParams, products]);

  // Check if product has analyzed ingredients (has ratings)
  const hasAnalyzedIngredients = (product) => {
    if (!product?.ingredients?.length) return false;
    const hasRating = product.ingredients.some(ing => 
      typeof ing === 'object' && ing.rating !== undefined && ing.rating !== null
    );
    return hasRating;
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(products.filter(p => p.id !== productId));
      setDeleteProductId(null);
      toast.success("Product deleted");
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const toggleShelve = async (productId, currentShelved) => {
    try {
      const newShelved = !currentShelved;
      await axios.put(`${API}/products/${productId}/shelve?shelved=${newShelved}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the product in state
      setProducts(products.map(p => 
        p.id === productId ? { ...p, shelved: newShelved } : p
      ));
      
      // Update selected product if it's the one being shelved
      if (selectedProduct?.id === productId) {
        setSelectedProduct({ ...selectedProduct, shelved: newShelved });
      }
      
      toast.success(newShelved ? "Product shelved - excluded from routines" : "Product unshelved - back in rotation");
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const analyzeProduct = async (productId) => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/products/${productId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000 // 60 second timeout for AI analysis
      });
      
      // Update the product in state
      setProducts(products.map(p => 
        p.id === productId 
          ? { ...p, ingredients: response.data.ingredients, analyzed: true }
          : p
      ));
      
      // Update selected product if it's the one being analyzed
      if (selectedProduct?.id === productId) {
        setSelectedProduct({ 
          ...selectedProduct, 
          ingredients: response.data.ingredients, 
          analyzed: true 
        });
      }
      
      toast.success("Product analyzed! Ingredient ratings are now available.");
    } catch (error) {
      console.error("Failed to analyze product:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze product");
    } finally {
      setAnalyzing(false);
    }
  };

  // Auto-analyze when product is selected and not analyzed
  useEffect(() => {
    if (selectedProduct && !analyzing) {
      const needsAnalysis = !hasAnalyzedIngredients(selectedProduct) && 
                           selectedProduct.ingredients?.length > 0;
      if (needsAnalysis) {
        analyzeProduct(selectedProduct.id);
      }
    }
  }, [selectedProduct?.id]);

  // Get unique categories and brands for filters
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();
  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
  
  // Brand letter ranges
  const brandLetterRanges = [
    { label: "A-F", start: "a", end: "f" },
    { label: "G-L", start: "g", end: "l" },
    { label: "M-R", start: "m", end: "r" },
    { label: "S-Z", start: "s", end: "z" }
  ];

  // Status counts
  const statusCounts = {
    keep: products.filter(p => p.status === 'keep' && !p.shelved).length,
    alternate: products.filter(p => p.status === 'alternate' && !p.shelved).length,
    shelved: products.filter(p => p.shelved).length,
    unanalyzed: products.filter(p => !hasAnalyzedIngredients(p) && !p.shelved).length
  };

  // Analyze all unanalyzed products
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const analyzeAllProducts = async () => {
    const unanalyzedProducts = products.filter(p => !hasAnalyzedIngredients(p) && !p.shelved && p.ingredients?.length > 0);
    if (unanalyzedProducts.length === 0) {
      toast.info("All products are already analyzed!");
      return;
    }
    
    setAnalyzingAll(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const product of unanalyzedProducts) {
      try {
        const response = await axios.post(`${API}/products/${product.id}/analyze`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.analyzed) {
          successCount++;
          // Update local state
          setProducts(prev => prev.map(p => 
            p.id === product.id 
              ? { ...p, ingredients: response.data.ingredients, analyzed: true }
              : p
          ));
        }
      } catch (error) {
        failCount++;
        console.error(`Failed to analyze ${product.name}:`, error);
      }
    }
    
    setAnalyzingAll(false);
    if (successCount > 0) {
      toast.success(`Analyzed ${successCount} products!`);
    }
    if (failCount > 0) {
      toast.error(`Failed to analyze ${failCount} products`);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = statusFilter || categoryFilter !== "all" || brandFilter !== "all" || searchQuery;

  // ==== Expiration helpers ====
  const getExpirationStatus = (product) => {
    if (!product.expiration_date) return null;
    const exp = new Date(product.expiration_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return { kind: "expired", label: `Expired ${Math.abs(days)}d ago`, days };
    if (days <= 30) return { kind: "soon", label: `Expires in ${days}d`, days };
    if (days <= 90) return { kind: "watch", label: `Expires in ${days}d`, days };
    return { kind: "ok", label: `Expires in ${days}d`, days };
  };

  // ==== Purchase Links ====
  const buildPurchaseUrls = (product) => {
    const q = encodeURIComponent(`${product.brand || ""} ${product.name}`.trim());
    return {
      sephora: `https://www.sephora.com/search?keyword=${q}`,
      ulta: `https://www.ulta.com/shop/search?Ntt=${q}`,
      amazon: `https://www.amazon.com/s?k=${q}`
    };
  };

  // ==== Expiration update ====
  const [savingExpiration, setSavingExpiration] = useState(false);
  const updateExpiration = async (productId, dateStr) => {
    setSavingExpiration(true);
    try {
      await axios.put(
        `${API}/products/${productId}/expiration`,
        { expiration_date: dateStr || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, expiration_date: dateStr || null } : p));
      if (selectedProduct?.id === productId) {
        setSelectedProduct({ ...selectedProduct, expiration_date: dateStr || null });
      }
      toast.success(dateStr ? "Expiration date saved" : "Expiration date cleared");
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to update expiration";
      if (msg.includes("migration")) {
        toast.error("Database not ready. Run SQL migration in Supabase first.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSavingExpiration(false);
    }
  };

  const clearAllFilters = () => {
    setStatusFilter(null);
    setCategoryFilter("all");
    setBrandFilter("all");
    setSearchQuery("");
  };

  // Toggle pin status for AM/PM routine
  const togglePin = async (productId, timeOfDay, currentPinned, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      const newPinned = !currentPinned;
      const response = await axios.put(
        `${API}/products/${productId}/pin?time_of_day=${timeOfDay}&pinned=${newPinned}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setProducts(products.map(p => 
        p.id === productId 
          ? { ...p, is_pinned_am: response.data.is_pinned_am, is_pinned_pm: response.data.is_pinned_pm }
          : p
      ));
      
      toast.success(
        newPinned 
          ? `Pinned to ${timeOfDay.toUpperCase()} routine` 
          : `Unpinned from ${timeOfDay.toUpperCase()} routine`
      );
    } catch (error) {
      toast.error("Failed to update pin status");
    }
  };

  const filteredProducts = products
    .filter(p => {
      // Apply status filter (shelved is now a separate filter)
      if (statusFilter === 'shelved') {
        if (!p.shelved) return false;
      } else if (statusFilter) {
        if (p.status !== statusFilter || p.shelved) return false;
      }
      
      // Apply category filter
      if (categoryFilter !== "all" && p.category?.toLowerCase() !== categoryFilter.toLowerCase()) return false;
      
      // Apply brand filter (letter range or specific brand)
      if (brandFilter !== "all") {
        const brandLower = (p.brand || "").toLowerCase();
        const range = brandLetterRanges.find(r => r.label === brandFilter);
        if (range) {
          const firstLetter = brandLower.charAt(0);
          if (firstLetter < range.start || firstLetter > range.end) return false;
        } else if (brandLower !== brandFilter.toLowerCase()) {
          return false;
        }
      }
      
      // Apply search filter
      return !searchQuery || 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.brand?.toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "brand-asc":
          return (a.brand || "").localeCompare(b.brand || "");
        case "brand-desc":
          return (b.brand || "").localeCompare(a.brand || "");
        case "category":
          return (a.category || "").localeCompare(b.category || "");
        case "score-high":
          const scoreA = a.skiniq_score ?? -1;
          const scoreB = b.skiniq_score ?? -1;
          return scoreB - scoreA;
        case "score-low":
          const scoreLowA = a.skiniq_score ?? 999;
          const scoreLowB = b.skiniq_score ?? 999;
          return scoreLowA - scoreLowB;
        case "shelved":
          // Shelved items first, then by name
          if (a.shelved && !b.shelved) return -1;
          if (!a.shelved && b.shelved) return 1;
          return a.name.localeCompare(b.name);
        case "oldest":
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "newest":
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

  const ProductCardLocal = ({ product }) => (
    <ProductCard
      product={product}
      hasAnalyzedIngredients={hasAnalyzedIngredients}
      getExpirationStatus={getExpirationStatus}
      onSelect={setSelectedProduct}
      onTogglePin={togglePin}
      onAnalyze={analyzeProduct}
      onToggleShelve={toggleShelve}
      onRequestDelete={setDeleteProductId}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-pulse-soft text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
              <div className="w-8 h-8 rounded-full bg-primary/40"></div>
            </div>
            <p className="mt-4 text-muted-foreground">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="product-library">
      <Navbar />
      
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 animate-fade-in-up">
          <div>
            <h1 className="font-serif text-3xl text-primary mb-2">Product Library</h1>
            <p className="text-muted-foreground">
              {products.length} products in your collection
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate("/routines")}
              variant="outline"
              className="border-primary/30 hover:bg-primary/5"
              data-testid="routines-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Routines
            </Button>
            <Button 
              onClick={() => navigate("/add-product")}
              className="btn-primary"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Quick Links */}
        <div className="flex gap-3 mb-6 animate-fade-in-up">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate("/ingredients")}
            className="border-primary/20"
            data-testid="ingredient-reference-btn"
          >
            <Beaker className="w-4 h-4 mr-2" />
            Ingredient Reference
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={recalculateScores}
            disabled={recalculating}
            className="border-primary/20"
            data-testid="recalculate-scores-btn"
          >
            {recalculating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Recalculate Scores
              </>
            )}
          </Button>
          {statusCounts.unanalyzed > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={analyzeAllProducts}
              disabled={analyzingAll}
              className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
              data-testid="analyze-all-btn"
            >
              {analyzingAll ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <FlaskConical className="w-4 h-4 mr-2" />
                  Analyze All ({statusCounts.unanalyzed})
                </>
              )}
            </Button>
          )}
        </div>

        {/* Status Filter Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6 animate-fade-in-up">
          <button
            onClick={() => setStatusFilter(statusFilter === 'keep' ? null : 'keep')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'keep' 
                ? 'border-green-500 bg-green-50' 
                : 'border-border bg-white/50 hover:border-green-300'
            }`}
            data-testid="filter-keep"
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className={`w-5 h-5 ${statusFilter === 'keep' ? 'text-green-600' : 'text-green-500'}`} />
              <span className="font-medium text-primary">Keep</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{statusCounts.keep}</p>
            <p className="text-xs text-muted-foreground">products</p>
          </button>
          
          <button
            onClick={() => setStatusFilter(statusFilter === 'alternate' ? null : 'alternate')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'alternate' 
                ? 'border-amber-500 bg-amber-50' 
                : 'border-border bg-white/50 hover:border-amber-300'
            }`}
            data-testid="filter-alternate"
          >
            <div className="flex items-center gap-2 mb-1">
              <RotateCcw className={`w-5 h-5 ${statusFilter === 'alternate' ? 'text-amber-600' : 'text-amber-500'}`} />
              <span className="font-medium text-primary">Alternate</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{statusCounts.alternate}</p>
            <p className="text-xs text-muted-foreground">products</p>
          </button>
          
          <button
            onClick={() => setStatusFilter(statusFilter === 'shelved' ? null : 'shelved')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'shelved' 
                ? 'border-gray-500 bg-gray-100' 
                : 'border-border bg-white/50 hover:border-gray-400'
            }`}
            data-testid="filter-shelved"
          >
            <div className="flex items-center gap-2 mb-1">
              <Archive className={`w-5 h-5 ${statusFilter === 'shelved' ? 'text-gray-600' : 'text-gray-500'}`} />
              <span className="font-medium text-primary">Shelved</span>
            </div>
            <p className="text-2xl font-bold text-gray-600">{statusCounts.shelved}</p>
            <p className="text-xs text-muted-foreground">products</p>
          </button>
        </div>

        {/* Active Filter Badge */}
        {statusFilter && (
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="flex items-center gap-1">
              Filtered by: {statusFilter}
              <button 
                onClick={() => setStatusFilter(null)} 
                className="ml-1 hover:text-destructive"
              >
                ×
              </button>
            </Badge>
            <span className="text-sm text-muted-foreground">
              Showing {filteredProducts.length} of {products.length} products
            </span>
          </div>
        )}

        {/* Sorting & Filtering */}
        <div className="flex flex-col gap-4 mb-6 animate-fade-in-up stagger-1">
          {/* Search and Sort Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-skiniq"
                data-testid="search-input"
              />
            </div>
            
            {/* Sort Dropdown */}
            <Select value={sortBy} onValueChange={(value) => {
              setSortBy(value);
              // If "shelved" is selected, also set the status filter
              if (value === "shelved") {
                setStatusFilter("shelved");
              }
            }}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="sort-select">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
                <SelectItem value="shelved">Shelved Only</SelectItem>
                <SelectItem value="score-high">Score (High to Low)</SelectItem>
                <SelectItem value="score-low">Score (Low to High)</SelectItem>
                <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                <SelectItem value="brand-asc">Brand (A-Z)</SelectItem>
                <SelectItem value="brand-desc">Brand (Z-A)</SelectItem>
                <SelectItem value="category">Category</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Filter Row */}
          <div className="flex flex-wrap gap-3">
            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px]" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat.toLowerCase()} className="capitalize">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Brand Filter */}
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="w-[160px]" data-testid="brand-filter">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                <SelectItem value="A-F">Brands A-F</SelectItem>
                <SelectItem value="G-L">Brands G-L</SelectItem>
                <SelectItem value="M-R">Brands M-R</SelectItem>
                <SelectItem value="S-Z">Brands S-Z</SelectItem>
                {brands.length <= 15 && brands.map(brand => (
                  <SelectItem key={brand} value={brand}>
                    {brand}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearAllFilters}
                className="text-muted-foreground hover:text-destructive"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
          
          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {statusFilter && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Status: {statusFilter}
                  <button onClick={() => setStatusFilter(null)} className="ml-1 hover:text-destructive">×</button>
                </Badge>
              )}
              {categoryFilter !== "all" && (
                <Badge variant="secondary" className="flex items-center gap-1 capitalize">
                  {categoryFilter}
                  <button onClick={() => setCategoryFilter("all")} className="ml-1 hover:text-destructive">×</button>
                </Badge>
              )}
              {brandFilter !== "all" && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  {brandFilter}
                  <button onClick={() => setBrandFilter("all")} className="ml-1 hover:text-destructive">×</button>
                </Badge>
              )}
              {searchQuery && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery("")} className="ml-1 hover:text-destructive">×</button>
                </Badge>
              )}
              <span className="text-sm text-muted-foreground ml-2">
                ({filteredProducts.length} of {products.length})
              </span>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in-up stagger-2">
            {filteredProducts.map(product => (
              <ProductCardLocal key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-serif text-xl text-primary mb-2">
              {products.length === 0 ? "No Products Yet" : "No Results Found"}
            </h3>
            <p className="text-muted-foreground mb-6">
              {products.length === 0 
                ? "Add your first skincare product to get started" 
                : "Try adjusting your search or filters"}
            </p>
            {products.length === 0 && (
              <Button 
                onClick={() => navigate("/add-product")}
                className="btn-primary"
                data-testid="empty-add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            )}
          </div>
        )}
      </main>

      {/* Product Detail Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden glass" data-testid="product-detail-dialog" aria-describedby="product-detail-description">
          {selectedProduct && (
            <>
              <DialogHeader>
                <p id="product-detail-description" className="sr-only">Details and ingredients for {selectedProduct.name}</p>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="font-serif text-2xl text-primary">
                      {selectedProduct.name}
                    </DialogTitle>
                    {selectedProduct.brand && (
                      <p className="text-muted-foreground mt-1">{selectedProduct.brand}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedProduct.category && (
                      <Badge variant="outline" className="capitalize">
                        {selectedProduct.category}
                      </Badge>
                    )}
                    {selectedProduct.shelved && (
                      <Badge variant="secondary" className="bg-gray-200 text-gray-600">
                        <PackageX className="w-3 h-3 mr-1" />
                        Shelved
                      </Badge>
                    )}
                    {!hasAnalyzedIngredients(selectedProduct) && !selectedProduct.shelved && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                        Not analyzed
                      </Badge>
                    )}
                  </div>
                </div>
              </DialogHeader>
              
              {/* Shelve Toggle */}
              <div className={`rounded-lg p-4 flex items-center justify-between ${selectedProduct.shelved ? 'bg-gray-100 border border-gray-200' : 'bg-primary/5 border border-primary/20'}`}>
                <div>
                  <p className={`font-medium ${selectedProduct.shelved ? 'text-gray-700' : 'text-primary'}`}>
                    {selectedProduct.shelved ? "Product is Shelved" : "Product is Active"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedProduct.shelved 
                      ? "This product is excluded from routine generation (out of stock)" 
                      : "This product is included in routine generation"}
                  </p>
                </div>
                <Button 
                  variant={selectedProduct.shelved ? "default" : "outline"}
                  onClick={() => toggleShelve(selectedProduct.id, selectedProduct.shelved)}
                  className={selectedProduct.shelved ? "btn-primary" : ""}
                  data-testid="toggle-shelve-btn"
                >
                  <PackageX className="w-4 h-4 mr-2" />
                  {selectedProduct.shelved ? "Unshelve" : "Shelve"}
                </Button>
              </div>

              {/* Expiration Date */}
              <div className="rounded-lg p-4 border border-border bg-white/60">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarClock className="w-4 h-4 text-primary" />
                      <p className="font-medium text-primary text-sm">Expiration Date</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Track when this product expires to avoid using old skincare.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={selectedProduct.expiration_date || ""}
                      onChange={(e) => updateExpiration(selectedProduct.id, e.target.value)}
                      disabled={savingExpiration}
                      className="w-44"
                      data-testid="expiration-date-input"
                    />
                    {selectedProduct.expiration_date && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateExpiration(selectedProduct.id, null)}
                        disabled={savingExpiration}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid="clear-expiration-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {(() => {
                  const exp = getExpirationStatus(selectedProduct);
                  if (!exp) return null;
                  const cls = exp.kind === "expired"
                    ? "bg-rose-50 text-rose-700 border-rose-200"
                    : exp.kind === "soon"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200";
                  return (
                    <div className={`mt-3 px-3 py-2 rounded-md border text-sm ${cls}`}>
                      {exp.kind === "expired" ? "⚠️ This product is expired. Consider replacing it." : exp.label}
                    </div>
                  );
                })()}
              </div>

              {/* Where to Buy */}
              {(() => {
                const urls = buildPurchaseUrls(selectedProduct);
                return (
                  <div className="rounded-lg p-4 border border-border bg-white/60">
                    <div className="flex items-center gap-2 mb-3">
                      <ShoppingBag className="w-4 h-4 text-primary" />
                      <p className="font-medium text-primary text-sm">Where to Buy</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href={urls.sephora} target="_blank" rel="noopener noreferrer" data-testid="buy-sephora-btn">
                        <Button variant="outline" size="sm" className="border-black/20 hover:bg-black hover:text-white transition-all">
                          Sephora
                        </Button>
                      </a>
                      <a href={urls.ulta} target="_blank" rel="noopener noreferrer" data-testid="buy-ulta-btn">
                        <Button variant="outline" size="sm" className="border-rose-300 hover:bg-rose-500 hover:text-white transition-all">
                          Ulta
                        </Button>
                      </a>
                      <a href={urls.amazon} target="_blank" rel="noopener noreferrer" data-testid="buy-amazon-btn">
                        <Button variant="outline" size="sm" className="border-amber-300 hover:bg-amber-500 hover:text-white transition-all">
                          Amazon
                        </Button>
                      </a>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Opens search results in a new tab.
                    </p>
                  </div>
                );
              })()}
              
              {/* Auto-analyzing state */}
              {analyzing && !hasAnalyzedIngredients(selectedProduct) && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <RefreshCw className="w-6 h-6 text-primary animate-spin" />
                    <p className="font-medium text-primary">Analyzing Ingredients...</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Getting safety ratings, benefits, and concerns for each ingredient.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    This usually takes 5-10 seconds
                  </p>
                </div>
              )}
              
              {/* Manual analyze button - only show if auto-analyze failed or no ingredients */}
              {!analyzing && !hasAnalyzedIngredients(selectedProduct) && selectedProduct.ingredients?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-amber-800">Analysis Ready</p>
                    <p className="text-sm text-amber-700">Click to retry ingredient analysis.</p>
                  </div>
                  <Button 
                    onClick={() => analyzeProduct(selectedProduct.id)}
                    disabled={analyzing}
                    className="btn-primary"
                    data-testid="analyze-product-btn"
                  >
                    <FlaskConical className="w-4 h-4 mr-2" />
                    Retry Analysis
                  </Button>
                </div>
              )}
              
              <ScrollArea className="max-h-[60vh] pr-4">
                <div className="space-y-6 py-4">
                  {/* Tips & Warnings from ingredients */}
                  {hasAnalyzedIngredients(selectedProduct) && (
                    <>
                      {/* Warnings - ingredients with low ratings */}
                      {selectedProduct.ingredients?.some(ing => ing.rating && ing.rating <= 2) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            <h4 className="font-medium text-red-800">Ingredients of Concern</h4>
                          </div>
                          <ul className="space-y-1">
                            {selectedProduct.ingredients
                              .filter(ing => ing.rating && ing.rating <= 2)
                              .map((ing, idx) => (
                                <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                                  <span className="text-red-400">•</span>
                                  <span><strong>{ing.name}</strong> (★{ing.rating}) - {ing.concerns?.[0] || 'May cause sensitivity'}</span>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Tips - beneficial ingredients */}
                      {selectedProduct.ingredients?.some(ing => ing.rating && ing.rating >= 4) && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Lightbulb className="w-5 h-5 text-green-600" />
                            <h4 className="font-medium text-green-800">Key Benefits</h4>
                          </div>
                          <ul className="space-y-1">
                            {selectedProduct.ingredients
                              .filter(ing => ing.rating && ing.rating >= 4)
                              .slice(0, 4)
                              .map((ing, idx) => (
                                <li key={idx} className="text-sm text-green-700 flex items-start gap-2">
                                  <span className="text-green-400">•</span>
                                  <span><strong>{ing.name}</strong> - {ing.benefits?.[0] || ing.function || 'Beneficial ingredient'}</span>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  {/* Ingredients */}
                  <div className="mt-6">
                    <h4 className="font-medium text-primary mb-3">
                      Ingredients ({selectedProduct.ingredients?.length || 0})
                      {hasAnalyzedIngredients(selectedProduct) && (
                        <span className="text-xs text-green-600 ml-2">✓ Analyzed</span>
                      )}
                    </h4>
                    {selectedProduct.ingredients && selectedProduct.ingredients.length > 0 ? (
                      <div className="space-y-3">
                        {selectedProduct.ingredients.map((ingredient, idx) => (
                          <IngredientCard 
                            key={idx} 
                            ingredient={typeof ingredient === 'string' ? { name: ingredient } : ingredient} 
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No ingredients analyzed yet</p>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProductId} onOpenChange={() => setDeleteProductId(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteProduct(deleteProductId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
